import { Box, Button, Checkbox, Dialog, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import { useCallback, useEffect, useState } from 'react'
import { apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import MosInfoProvider from '../Widgets/common/provider/MosInfoProvider'
import WidgetGrid from '../Widgets/base/WidgetGrid'
import { serializeTemplateWidgets } from '../widgetTemplates/widgetTemplateUtils'
import { stripWidgetSettings } from '../widgetTemplates/widgetTemplateParse'

const HEADER_HEIGHT = '3.5rem'
const ENDPOINT_BASE = 'widgets/initialScreenTemplates'

/**
 * Full-screen editor to compose a brand-new initial-screen template, opened from
 * the Users "Initial screen templates" modal. Pressing the grid's Save asks
 * whether to only save the template (POST widgets/initialScreenTemplates) or to
 * also apply it to the user the modal was opened from (POST .../apply/{userId}).
 * The grid deliberately does NOT pass showAllWidgets, so the catalog only offers
 * widgets valid without a device (availableWithoutDevice).
 *
 * onClose is called with 'applied' (created + applied to the user), 'saved'
 * (created only) or null (cancelled).
 */
const CreateInitialScreenTemplateDialog = ({ open, user, onClose }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [name, setName] = useState('')
  const [widgets, setWidgets] = useState([])
  const [editing, setEditing] = useState(true)
  const [choiceOpen, setChoiceOpen] = useState(false)
  const [copySettings, setCopySettings] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) {
      setName('')
      setWidgets([])
      setEditing(true)
      setChoiceOpen(false)
      setCopySettings(true)
    }
  }, [open])

  const reloadWidgets = useCallback(() => setWidgets([]), [])

  const onSave = useCallback(() => {
    if (!name.trim()) {
      showAlert({ title: t_('A name is required'), message: '', severity: 'error' })
      return
    }
    if (!Array.isArray(widgets) || widgets.length === 0) {
      showAlert({ title: t_('Add at least one widget to save the current layout as a template'), message: '', severity: 'error' })
      return
    }
    setChoiceOpen(true)
  }, [name, widgets, showAlert, t_])

  const createTemplate = () => apiPost(ENDPOINT_BASE, { name: name.trim(), widgets: serializeTemplateWidgets(copySettings ? widgets : stripWidgetSettings(widgets)) })

  const handleSaveOnly = async () => {
    setChoiceOpen(false)
    setBusy(true)
    try {
      await createTemplate()
      showAlert({ title: t_('Template created'), message: '', severity: 'success' })
      onClose('saved')
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const handleSaveAndUse = async () => {
    setChoiceOpen(false)
    setBusy(true)
    try {
      const created = await createTemplate()
      if (user && created && created.id) {
        await apiPost(ENDPOINT_BASE + '/' + created.id + '/apply/' + user.id, {})
      }
      showAlert({ title: t_('Template created'), message: '', severity: 'success' })
      onClose('applied')
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog fullScreen open={open} onClose={() => onClose(null)} PaperProps={{ sx: { overflow: 'hidden' } }}>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ px: 2, height: HEADER_HEIGHT, flexShrink: 0, boxSizing: 'border-box', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title={t_('Back')}>
          <IconButton onClick={() => onClose(null)} aria-label={t_('Back')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant='h6' sx={{ whiteSpace: 'nowrap' }}>{t_('Create new template')}</Typography>
        <TextField
          size='small'
          label={t_('Name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          sx={{ minWidth: '16rem' }}
          inputProps={{ maxLength: 255 }}
        />
        <FormControlLabel
          control={<Checkbox checked={copySettings} onChange={(event) => setCopySettings(event.target.checked)} size='small' />}
          label={t_('Copy widget settings')}
          sx={{ whiteSpace: 'nowrap' }}
        />
        <Typography variant='caption' color='text.secondary' noWrap sx={{ flexGrow: 1, textAlign: 'right' }}>
          {user ? user.username : ''}
        </Typography>
      </Stack>
      <Box sx={{ height: 'calc(100vh - ' + HEADER_HEIGHT + ')', overflow: 'auto' }}>
        <Stack direction='column' sx={{ height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <div style={{ position: 'sticky', top: '0.5rem', zIndex: 100, padding: '0 1rem' }}>{AlertElement}</div>
            <MosInfoProvider>
              <WidgetGrid
                widgets={widgets}
                setWidgets={setWidgets}
                reloadWidgets={reloadWidgets}
                onSave={onSave}
                editing={editing}
                setEditing={setEditing}
                toolbarLabel={user?.username}
              />
            </MosInfoProvider>
          </Box>
        </Stack>
      </Box>

      <SmartDialog
        setOpen={choiceOpen}
        title={createDialogTitle({ color: 'primary', title: t_('Save template'), icon: SaveIcon })}
        message={t_("Apply the new template to this user's initial screen?")}
        accept={false}
        cancel
        cancelCallback={() => setChoiceOpen(false)}
        renderCustomButtons={
          <Stack direction='row' spacing={1}>
            <Button variant='outlined' onClick={handleSaveOnly} disabled={busy}>
              {t_('Save without assigning')}
            </Button>
            <Button variant='contained' onClick={handleSaveAndUse} disabled={busy || !user}>
              {t_('Save and apply to this user')}
            </Button>
          </Stack>
        }
      />
    </Dialog>
  )
}

export default CreateInitialScreenTemplateDialog
